import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import Button from '../../../components/Button';
import { ArrowLeft, Plus, GripVertical, Copy, Trash2, ChevronDown, ChevronUp, ImageIcon, Loader2, FileText, X } from 'lucide-react';
import { useAuth } from '../../../context/AuthContext';
import VitaminSegmentSelector from '../../../components/segments/VitaminSegmentSelector';

/* ═══════════════════════════════════════════════════
   Types
   ═══════════════════════════════════════════════════ */

interface SegmentPersonaFilters {
  industryL1: string[];
  industryL2: string[];
  functions: string[];
  roles: string[];
}

interface VitaminConceptTestFormProps {
  onSubmit: (simulationId: number) => void;
  selectedSegmentIds: number[];
  personaFilters: Record<number, SegmentPersonaFilters>;
  onBack: () => void;
  onEditStep?: () => void;
  initialFormData?: Record<string, any>;
  onFormDataChange?: (data: Record<string, any>) => void;
  onSavePayload?: (payload: Record<string, any>) => void | Promise<void>;
}

interface PopulationOption {
  name: string;
  agent_count: number;
}

type ConceptTestMode = 'single' | 'multi';

interface VitaminConcept {
  id: string;
  conceptStr: string;
}

const API_URL = import.meta.env.VITE_API_URL || '';

const BRAND = {
  accent: '#07E5D1',
  accentLight: '#E8FCFA',
  dark: '#012e2a',
};

// Segment options for Smartypants users with population_vitamin_survey_enriched_copy
const SEGMENT_OPTIONS = [
  {
    name: "Moms with Kids (4-12)",
    description: "Mothers whose children are exclusively school-aged (4-12).",
    profiles: 59
  },
  {
    name: "Moms with Teens (13-19)",
    description: "Mothers whose children are exclusively adolescents aged 13-19.",
    profiles: 21
  },
  {
    name: "Moms with Kids and Teens",
    description: "Mothers with children in BOTH age groups (4-12 and 13-19).",
    profiles: 21
  },
  {
    name: "Teens (13-19)",
    description: "Teenagers aged 13-19 who take vitamins themselves.",
    profiles: 25
  }
];

// Demo user populations (actual counts for API, displayed as n=1000)
const DEMO_USER_POPULATIONS = [
  { name: 'population_mondelez_cookies', agent_count: 71 },
  { name: 'population_vitamin_survey_enriched_copy', agent_count: 126 }
];
const DEMO_DISPLAY_N_AGENTS = 1000;

// Internal form steps
type FormStep = 'concept-details' | 'segment-selection';

/* ═══════════════════════════════════════════════════
   Concept Template
   ═══════════════════════════════════════════════════ */

const CONCEPT_TEMPLATE = `
**Recommended Template for Concept Details:**

**Product Headline:** SmartyPants Teen Guy Complete Multivitamin Gummies

**Brand Name:** SmartyPants

**One Line Description:** Teen boys need targeted nutrition during rapid growth and development, but most don't get enough from diet alone.

**Detailed Description:** Premium gummy multivitamin specifically formulated for teen boys ages 13-19. Supports brain health, bone growth, immune function, and energy metabolism with omega-3s, vitamin D3, B12, and zinc in a delicious natural berry flavor that teens actually want to take.

**Reasons to Believe:**
- Omega-3 EPA & DHA from sustainable fish oil for brain health
- Vitamin D3 (1000 IU) for strong bones during growth spurts
- B12 and folate for energy and focus during school
- No synthetic colors, artificial flavors, or high-fructose corn syrup
- Third-party lab tested for purity and potency

**Price & Pack:** $24.99 for 90 gummies / 30-day supply

**Alternative Products:** OLLY Teen Boy Multi, Nature Made Teen Gummies, Centrum Teen`;

/* ═══════════════════════════════════════════════════
   Helper Functions
   ═══════════════════════════════════════════════════ */

const createEmptyConcept = (): VitaminConcept => ({
  id: crypto.randomUUID(),
  conceptStr: '',
});

const conceptFromInitialData = (data: Record<string, any>): VitaminConcept => {
  // If conceptStr exists, use it directly
  if (data.conceptStr) {
    return {
      id: crypto.randomUUID(),
      conceptStr: data.conceptStr,
    };
  }

  // Convert legacy structured data to text format with bold headings
  const parts: string[] = [];
  if (data.productHeadline) parts.push(`**Product Headline:** ${data.productHeadline}`);
  if (data.brandName) parts.push(`**Brand Name:** ${data.brandName}`);
  if (data.consumerInsight) parts.push(`**One Line Description:** ${data.consumerInsight}`);
  if (data.mainBenefit) parts.push(`**Detailed Description:** ${data.mainBenefit}`);

  const rtbs = Array.isArray(data.reasonsToBelieve)
    ? data.reasonsToBelieve.filter(Boolean)
    : data.reasonsToBelieve ? [data.reasonsToBelieve] : [];
  if (rtbs.length > 0) {
    parts.push(`**Reasons to Believe:**\n${rtbs.map((r: string) => `- ${r}`).join('\n')}`);
  }

  const pricePack = [data.pricePoint, data.packSize].filter(Boolean).join(' for ');
  if (pricePack) parts.push(`**Price & Pack:** ${pricePack}`);
  if (data.productImageUrl) parts.push(`**Hero Image URL:** ${data.productImageUrl}`);

  const comps = Array.isArray(data.competitorProducts)
    ? data.competitorProducts.filter(Boolean)
    : data.competitorProducts ? [data.competitorProducts] : [];
  if (comps.length > 0) {
    parts.push(`**Alternative Products:** ${comps.join(', ')}`);
  }

  return {
    id: crypto.randomUUID(),
    conceptStr: parts.join('\n\n'),
  };
};

// Format parsed image data into concept text with bold headings
const formatParsedDataToText = (parsed: Record<string, any>): string => {
  const parts: string[] = [];
  if (parsed.productHeadline) parts.push(`**Product Headline:** ${parsed.productHeadline}`);
  if (parsed.brandName) parts.push(`**Brand Name:** ${parsed.brandName}`);
  if (parsed.consumerInsight) parts.push(`**One Line Description:** ${parsed.consumerInsight}`);
  if (parsed.mainBenefit) parts.push(`**Detailed Description:** ${parsed.mainBenefit}`);

  const rtbs = parsed.reasonsToBelieve?.filter(Boolean) || [];
  if (rtbs.length > 0) {
    parts.push(`**Reasons to Believe:**\n${rtbs.map((r: string) => `- ${r}`).join('\n')}`);
  }

  const pricePack = [parsed.pricePoint, parsed.packSize].filter(Boolean).join(' for ');
  if (pricePack) parts.push(`**Price & Pack:** ${pricePack}`);

  return parts.join('\n\n');
};

/* ═══════════════════════════════════════════════════
   Upload Concept Modal Component
   ═══════════════════════════════════════════════════ */

interface UploadConceptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpload: (file: File) => void;
  isUploading: boolean;
}

const UploadConceptModal: React.FC<UploadConceptModalProps> = ({
  isOpen,
  onClose,
  onUpload,
  isUploading,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);

  // Reset image loaded state when modal opens
  useEffect(() => {
    if (isOpen) {
      setImageLoaded(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-xl shadow-2xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Upload Concept Image</h3>
          <button
            type="button"
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Sample Image Section */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Sample Concept Card</h4>
            <p className="text-xs text-gray-500 mb-3">
              Your concept image should include product details like the example below
            </p>
            {/* Fixed height container for sample image */}
            <div className="border rounded-lg p-3 bg-gray-50 h-96 flex items-center justify-center">
              {!imageLoaded && (
                <div className="flex flex-col items-center text-gray-400">
                  <Loader2 className="w-8 h-8 animate-spin mb-2" />
                  <span className="text-sm">Loading sample...</span>
                </div>
              )}
              <img
                src="/assets/sample-concept-card.png"
                alt="Sample concept card"
                className={`max-h-[22rem] w-auto rounded-md object-contain ${imageLoaded ? 'block' : 'hidden'}`}
                onLoad={() => setImageLoaded(true)}
              />
            </div>
          </div>

          {/* Upload Section */}
          <div>
            <h4 className="text-sm font-medium text-gray-700 mb-2">Upload Your Concept</h4>
            <label
              className={`flex flex-col items-center justify-center w-full h-40 border-2 border-dashed rounded-lg cursor-pointer transition-colors ${
                isUploading
                  ? 'border-gray-300 bg-gray-50 cursor-wait'
                  : 'border-gray-300 hover:border-[#07E5D1] hover:bg-[#E8FCFA]'
              }`}
            >
              <div className="flex flex-col items-center justify-center py-4">
                {isUploading ? (
                  <>
                    <Loader2 className="w-10 h-10 animate-spin mb-2" style={{ color: BRAND.accent }} />
                    <p className="text-sm text-gray-500">Parsing image...</p>
                  </>
                ) : (
                  <>
                    <ImageIcon className="w-10 h-10 text-gray-400 mb-2" />
                    <p className="text-sm text-gray-500">
                      <span className="font-medium" style={{ color: BRAND.accent }}>Click to upload</span> or drag and drop
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      PNG, JPG, WebP, GIF up to 50MB
                    </p>
                  </>
                )}
              </div>
              <input
                type="file"
                className="hidden"
                accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.gif,.heic,.heif"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) onUpload(file);
                  e.target.value = '';
                }}
                disabled={isUploading}
              />
            </label>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   ConceptCard Component
   ═══════════════════════════════════════════════════ */

interface ConceptCardProps {
  concept: VitaminConcept;
  index: number;
  totalCount: number;
  isExpanded: boolean;
  onToggleExpand: () => void;
  onUpdate: (conceptStr: string) => void;
  onPrefillTemplate: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  canDelete: boolean;
  showActions: boolean;
  // Drag props
  onDragStart: (e: React.DragEvent) => void;
  onDragEnd: (e: React.DragEvent) => void;
  onDragOver: (e: React.DragEvent) => void;
  onDrop: (e: React.DragEvent) => void;
  isDragging: boolean;
  isDropTarget: boolean;
  // Image parsing
  onParseImage: (file: File) => void;
  isParsingImage: boolean;
}

const ConceptCard: React.FC<ConceptCardProps> = ({
  concept,
  index,
  isExpanded,
  onToggleExpand,
  onUpdate,
  onPrefillTemplate,
  onDuplicate,
  onDelete,
  canDelete,
  showActions,
  onDragStart,
  onDragEnd,
  onDragOver,
  onDrop,
  isDragging,
  isDropTarget,
  onParseImage,
  isParsingImage,
}) => {
  // Modal state for upload concept
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [wasParsingImage, setWasParsingImage] = useState(false);

  // Refs for scroll sync
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Sync scroll between textarea and overlay
  const handleScroll = useCallback(() => {
    if (textareaRef.current && overlayRef.current) {
      overlayRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  }, []);

  // Close modal when parsing completes
  useEffect(() => {
    if (isParsingImage) {
      setWasParsingImage(true);
    } else if (wasParsingImage && !isParsingImage) {
      // Parsing just finished
      setIsUploadModalOpen(false);
      setWasParsingImage(false);
    }
  }, [isParsingImage, wasParsingImage]);

  // Handle upload from modal
  const handleModalUpload = (file: File) => {
    onParseImage(file);
  };

  // Extract brand name from concept text for display in header
  const brandNameMatch = concept.conceptStr.match(/Brand Name:\s*(.+?)(?:\n|$)/);
  const brandName = brandNameMatch ? brandNameMatch[1].trim() : '';

  // Extract product headline for preview
  const headlineMatch = concept.conceptStr.match(/Product Headline:\s*(.+?)(?:\n|$)/);
  const productHeadline = headlineMatch ? headlineMatch[1].trim() : '';

  return (
    <div
      draggable={showActions}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      onDragOver={onDragOver}
      onDrop={onDrop}
      className={`border rounded-lg transition-all ${
        isDragging ? 'opacity-50' : ''
      } ${isDropTarget ? 'border-[#07E5D1] bg-[#E8FCFA]/30' : 'border-gray-200 bg-white'}`}
    >
      {/* Card Header */}
      <div
        className="flex items-center gap-3 px-4 py-3 cursor-pointer"
        style={{ borderBottom: isExpanded ? '1px solid #e5e7eb' : 'none' }}
        onClick={onToggleExpand}
      >
        {showActions && (
          <button
            type="button"
            className="cursor-grab active:cursor-grabbing text-gray-400 hover:text-gray-600"
            onMouseDown={(e) => e.stopPropagation()}
          >
            <GripVertical className="w-5 h-5" />
          </button>
        )}

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span
              className="text-xs font-semibold px-2 py-0.5 rounded-full"
              style={{ backgroundColor: BRAND.accentLight, color: BRAND.dark }}
            >
              Concept {index + 1}
            </span>
            {brandName ? (
              <span className="text-sm font-medium text-gray-900 truncate">
                {brandName}
              </span>
            ) : (
              <span className="text-sm text-gray-400 italic">Untitled concept</span>
            )}
          </div>
          {productHeadline && !isExpanded && (
            <p className="text-xs text-gray-500 mt-0.5 truncate">{productHeadline}</p>
          )}
        </div>

        <div className="flex items-center gap-1">
          {showActions && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onDuplicate(); }}
                className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
                title="Duplicate"
              >
                <Copy className="w-4 h-4" />
              </button>
              {canDelete && (
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); onDelete(); }}
                  className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                  title="Delete"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </>
          )}
          <button type="button" className="p-1 text-gray-400">
            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {/* Card Body (Expandable) */}
      {isExpanded && (
        <div className="px-4 py-4 space-y-4">
          {/* Action buttons row */}
          <div className="flex items-center gap-2">
            {/* Upload concept button - opens modal */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setIsUploadModalOpen(true); }}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <ImageIcon className="w-4 h-4" />
              Upload Concept
            </button>

            {/* Prefill button */}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onPrefillTemplate(); }}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <FileText className="w-4 h-4" />
              Prefilled Template
            </button>
          </div>

          {/* Helper text - shown above input */}
          <p className="text-sm text-gray-500 bg-gray-50 px-3 py-2 rounded-lg border border-gray-200">
          Upload a concept card or use the pre-filled template to share the details of the concept you want to test.
          </p>

          {/* Concept Details - Styled Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Concept Details <span className="text-red-500">*</span>
            </label>
            <div className="relative border border-gray-300 rounded-lg shadow-sm focus-within:ring-2 focus-within:ring-[#07E5D1] focus-within:border-[#07E5D1]">
              {/* Styled overlay that shows **text** as darker (without the **) */}
              <div
                ref={overlayRef}
                className="absolute inset-0 px-4 py-3 pointer-events-none overflow-y-auto text-sm leading-relaxed whitespace-pre-wrap break-words"
                aria-hidden="true"
              >
                {concept.conceptStr.split(/(\*\*[^*]+\*\*)/).map((part, i) => {
                  if (part.startsWith('**') && part.endsWith('**')) {
                    // This is a **field** - show darker without the ** markers
                    const innerText = part.slice(2, -2);
                    return (
                      <span key={i} className="text-gray-900 font-semibold">
                        {innerText}
                      </span>
                    );
                  }
                  return <span key={i} className="text-gray-700">{part}</span>;
                })}
              </div>
              {/* Actual textarea - transparent text so overlay shows through */}
              <textarea
                ref={textareaRef}
                rows={16}
                className="relative block w-full px-4 py-3 border-0 rounded-lg text-sm leading-relaxed resize-y bg-transparent focus:outline-none focus:ring-0"
                style={{ caretColor: '#1C1C1E', color: 'transparent', WebkitTextFillColor: 'transparent' }}
                value={concept.conceptStr}
                onChange={(e) => onUpdate(e.target.value)}
                onScroll={handleScroll}
                placeholder=""
              />
              {/* Placeholder when empty */}
              {!concept.conceptStr && (
                <div className="absolute top-3 left-4 text-gray-400 pointer-events-none text-sm leading-relaxed whitespace-pre-wrap">
                  <span className="text-gray-500 font-semibold">Product Headline:</span> Your product name{'\n\n'}
                  <span className="text-gray-500 font-semibold">Brand Name:</span> Your brand{'\n\n'}
                  <span className="text-gray-500 font-semibold">One Line Description:</span> Brief overview
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Upload Concept Modal */}
      <UploadConceptModal
        isOpen={isUploadModalOpen}
        onClose={() => setIsUploadModalOpen(false)}
        onUpload={handleModalUpload}
        isUploading={isParsingImage}
      />
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   Main Form Component
   ═══════════════════════════════════════════════════ */

const VitaminConceptTestForm: React.FC<VitaminConceptTestFormProps> = ({
  onBack,
  onEditStep,
  initialFormData = {},
  onFormDataChange = () => {},
  onSavePayload,
}) => {
  // Auth state
  const { isAdmin, isSmartypantsUser, isBacchusUser, isDemoUser } = useAuth();

  // Mode state
  const [mode, setMode] = useState<ConceptTestMode>('single');

  // Concepts state
  const [concepts, setConcepts] = useState<VitaminConcept[]>(() => {
    if (initialFormData.concepts && Array.isArray(initialFormData.concepts)) {
      return initialFormData.concepts.map(conceptFromInitialData);
    }
    return [conceptFromInitialData(initialFormData)];
  });

  // Shared config state
  const [simName, setSimName] = useState(initialFormData.simName || '');
  const [sampleSize, setSampleSize] = useState<number>(initialFormData.sampleSize ?? 126);
  const [population, setPopulation] = useState(initialFormData.population || '');
  const [populations, setPopulations] = useState<PopulationOption[]>([]);
  const [populationsLoading, setPopulationsLoading] = useState(true);
  const [selectedSegments, setSelectedSegments] = useState<string[]>(initialFormData.segments || []);

  // Determine if segment step should be shown (for Smartypants users with enriched copy population)
  const needsSegmentStep = isSmartypantsUser && population === 'population_vitamin_survey_enriched_copy';

  // Internal step state
  const [currentStep, setCurrentStep] = useState<FormStep>('concept-details');

  // UI state
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set([concepts[0]?.id]));
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [parsingImageForId, setParsingImageForId] = useState<string | null>(null);

  // Fetch populations (admin-only) or set default for non-admins
  useEffect(() => {
    if (!isAdmin) {
      // Demo users get dropdown with 2 population options
      if (isDemoUser) {
        setPopulations(DEMO_USER_POPULATIONS);
        setPopulation(DEMO_USER_POPULATIONS[0].name);
        setSampleSize(71);
        setPopulationsLoading(false);
        return;
      }
      // Bacchus users get fixed population (no dropdown)
      if (isBacchusUser) {
        setPopulation('population_mondelez_cookies');
        setSampleSize(71);
        setPopulationsLoading(false);
        return;
      }
      // Other non-admin users get vitamin population
      setPopulation('population_vitamin_survey_enriched_copy');
      setPopulationsLoading(false);
      return;
    }

    const fetchPopulations = async () => {
      try {
        const resp = await fetch(`${API_URL}/vitamin/populations`, { credentials: 'include' });
        if (resp.ok) {
          const data = await resp.json();
          const pops: PopulationOption[] = data.populations || data || [];
          setPopulations(pops);
          if (!population && pops.length > 0) {
            setPopulation(pops[0].name);
          }
        }
      } catch (e) {
        console.error('Failed to fetch populations:', e);
      } finally {
        setPopulationsLoading(false);
      }
    };
    fetchPopulations();
  }, [isAdmin, isBacchusUser, isDemoUser]);

  // Sync form data changes
  useEffect(() => {
    onFormDataChange({
      mode,
      simName,
      sampleSize,
      population,
      segments: selectedSegments,
      concepts: concepts.map(c => ({
        conceptStr: c.conceptStr,
      })),
    });
  }, [mode, simName, sampleSize, population, selectedSegments, concepts]);

  // Clear segments when population changes away from enriched copy
  useEffect(() => {
    if (population !== 'population_vitamin_survey_enriched_copy') {
      setSelectedSegments([]);
    }
  }, [population]);

  // Update sample size when demo user changes population
  useEffect(() => {
    if (isDemoUser && population) {
      const selectedPop = DEMO_USER_POPULATIONS.find(p => p.name === population);
      if (selectedPop) {
        setSampleSize(selectedPop.agent_count);
      }
    }
  }, [isDemoUser, population]);

  const handleFormChange = () => {
    if (onEditStep) onEditStep();
  };

  // Toggle segment selection
  const toggleSegment = (segment: string) => {
    setSelectedSegments(prev =>
      prev.includes(segment)
        ? prev.filter(s => s !== segment)
        : [...prev, segment]
    );
    handleFormChange();
  };

  // Select all segments
  const selectAllSegments = () => {
    setSelectedSegments(SEGMENT_OPTIONS.map(s => s.name));
    handleFormChange();
  };

  // Clear all segments
  const clearAllSegments = () => {
    setSelectedSegments([]);
    handleFormChange();
  };

  // Concept management functions
  const updateConcept = (id: string, conceptStr: string) => {
    setConcepts(prev => prev.map(c => (c.id === id ? { ...c, conceptStr } : c)));
    handleFormChange();
  };

  const prefillTemplate = (id: string) => {
    updateConcept(id, CONCEPT_TEMPLATE);
  };

  const duplicateConcept = (id: string) => {
    const source = concepts.find(c => c.id === id);
    if (!source) return;
    const duplicate: VitaminConcept = {
      ...structuredClone(source),
      id: crypto.randomUUID(),
    };
    const sourceIndex = concepts.findIndex(c => c.id === id);
    setConcepts(prev => [
      ...prev.slice(0, sourceIndex + 1),
      duplicate,
      ...prev.slice(sourceIndex + 1),
    ]);
    setExpandedIds(prev => new Set([...prev, duplicate.id]));
    handleFormChange();
  };

  const removeConcept = (id: string) => {
    if (mode === 'multi' && concepts.length <= 2) return;
    if (mode === 'single') return;
    setConcepts(prev => prev.filter(c => c.id !== id));
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.delete(id);
      return next;
    });
    handleFormChange();
  };

  const insertConceptAt = (index: number) => {
    const newConcept = createEmptyConcept();
    setConcepts(prev => [
      ...prev.slice(0, index),
      newConcept,
      ...prev.slice(index),
    ]);
    setExpandedIds(prev => new Set([...prev, newConcept.id]));
    handleFormChange();
  };

  const addConceptAtEnd = () => {
    insertConceptAt(concepts.length);
  };

  // Image parsing handler with dynamic field schema
  const handleParseImage = async (conceptId: string, file: File) => {
    // Validate file size (max 50MB)
    const MAX_SIZE_MB = 50;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      setError(`Image too large (${(file.size / (1024 * 1024)).toFixed(1)}MB). Maximum size is ${MAX_SIZE_MB}MB.`);
      return;
    }

    // Validate file type
    const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif', 'image/heic', 'image/heif'];
    if (!ALLOWED_TYPES.includes(file.type) && !file.name.match(/\.(jpg|jpeg|png|webp|gif|heic|heif)$/i)) {
      setError('Unsupported file type. Please use JPEG, PNG, WebP, GIF, or HEIC.');
      return;
    }

    setParsingImageForId(conceptId);
    setError(null);
    try {
      // Define field schema for dynamic parsing
      const fieldSchema = {
        fields: [
          { name: 'productHeadline', type: 'str', description: 'Product name/headline (main title)', required: true },
          { name: 'brandName', type: 'str', description: 'Brand/manufacturer name', required: true },
          { name: 'consumerInsight', type: 'str', description: 'One line Description - problem this solves', required: false },
          { name: 'mainBenefit', type: 'str', description: 'Detailed Description - main value proposition', required: true },
          { name: 'reasonsToBelieve', type: 'list[str]', description: 'Reasons to believe - key claims', required: false },
          { name: 'pricePoint', type: 'str', description: 'Price & Pack info', required: false },
          { name: 'packSize', type: 'str', description: 'Pack size/quantity', required: false },
        ],
        context: 'consumer product concept test'
      };

      const formData = new FormData();
      formData.append('image', file);
      formData.append('field_schema', JSON.stringify(fieldSchema));

      const response = await fetch(`${API_URL}/parse-image`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to parse image');
      }

      const parsed = await response.json();

      // Format parsed data into concept text and update
      const formattedText = formatParsedDataToText(parsed);
      setConcepts(prev => prev.map(c => {
        if (c.id !== conceptId) return c;
        return {
          ...c,
          conceptStr: formattedText || c.conceptStr,
        };
      }));

      // Expand the card to show parsed data
      setExpandedIds(prev => new Set([...prev, conceptId]));
      handleFormChange();
    } catch (err) {
      console.error('Image parsing failed:', err);
      setError(err instanceof Error ? err.message : 'Failed to parse image. Please try again or fill in manually.');
    } finally {
      setParsingImageForId(null);
    }
  };

  // Drag-and-drop handlers
  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '0.5';
    }
  };

  const handleDragEnd = (e: React.DragEvent) => {
    if (e.currentTarget instanceof HTMLElement) {
      e.currentTarget.style.opacity = '1';
    }
    setDraggedIndex(null);
    setDropTargetIndex(null);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;
    setDropTargetIndex(index);
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDropTargetIndex(null);
      return;
    }
    const reordered = Array.from(concepts);
    const [dragged] = reordered.splice(draggedIndex, 1);
    reordered.splice(dropIndex, 0, dragged);
    setConcepts(reordered);
    setDraggedIndex(null);
    setDropTargetIndex(null);
    handleFormChange();
  };

  // Mode switch handler
  const handleModeChange = (newMode: ConceptTestMode) => {
    setMode(newMode);
    if (newMode === 'multi' && concepts.length < 2) {
      // Add a second concept when switching to multi
      const newConcept = createEmptyConcept();
      setConcepts(prev => [...prev, newConcept]);
      setExpandedIds(prev => new Set([...prev, newConcept.id]));
    }
    handleFormChange();
  };

  // Validation - just check that concept text is not empty
  const validateConcept = (c: VitaminConcept): boolean =>
    c.conceptStr.trim().length > 0;

  const isFormValid = useMemo(() => {
    if (!population) return false;
    if (mode === 'single') return validateConcept(concepts[0]);
    return concepts.length >= 2 && concepts.every(validateConcept);
  }, [mode, concepts, population]);

  // Handle continuing to segment step or submitting
  const handleContinue = () => {
    if (needsSegmentStep && currentStep === 'concept-details') {
      setCurrentStep('segment-selection');
    } else {
      handleSubmit();
    }
  };

  // Handle going back from segment step
  const handleBackFromSegments = () => {
    setCurrentStep('concept-details');
  };

  // Submit handler
  const handleSubmit = async () => {
    if (!isFormValid || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);

    try {
      const basePayload = {
        task: 'vitamin-concept-test',
        name: simName || 'Concept Test',
        population,
        sample_size: sampleSize ?? 126,
      };

      let payload: Record<string, any>;
      if (mode === 'single') {
        payload = {
          ...basePayload,
          concept_str: concepts[0].conceptStr,
        };
      } else {
        payload = {
          ...basePayload,
          concepts: concepts.map(c => ({
            concept_str: c.conceptStr,
          })),
        };
      }

      // Add segments if any are selected (for Smartypants users)
      if (selectedSegments.length > 0) {
        payload.segments = selectedSegments;
      }

      if (onSavePayload) {
        await onSavePayload(payload);
      }
    } catch (err) {
      console.error('Error in form submission:', err);
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const selectClasses =
    'block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#07E5D1] focus:border-[#07E5D1] bg-white';
  const inputClasses =
    'block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#07E5D1] focus:border-[#07E5D1]';

  // Render concept details step (main form)
  const renderConceptDetailsStep = () => (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={onBack}
            icon={<ArrowLeft className="w-4 h-4 mr-1" />}
          >
            Back to use case selection
          </Button>
        </div>

        {error && (
          <div className="text-sm text-red-600 mb-3 bg-red-50 p-3 rounded-md">
            {error}
          </div>
        )}
      </div>

      {/* Mode Toggle */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Test Mode
        </label>
        <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50">
          <button
            type="button"
            onClick={() => handleModeChange('single')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
              mode === 'single'
                ? 'bg-white shadow-sm text-gray-900'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Single Concept
          </button>
          <button
            type="button"
            onClick={() => handleModeChange('multi')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-all ${
              mode === 'multi'
                ? 'bg-white shadow-sm text-gray-900'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Multi-Concept
          </button>
        </div>
        {mode === 'multi' && (
          <p className="mt-2 text-xs text-gray-500">
            Compare 2 or more concepts side-by-side. Get a winner analysis and comparison insights.
          </p>
        )}
      </div>

      {/* Simulation Name */}
      <div className="mb-6">
        <label htmlFor="simName" className="block text-sm font-medium text-gray-700 mb-2">
          Simulation Name (Optional)
        </label>
        <input
          id="simName"
          type="text"
          className={inputClasses}
          value={simName}
          onChange={(e) => { setSimName(e.target.value); handleFormChange(); }}
          placeholder="e.g., CalmKids Gummies — Wave 1"
        />
      </div>

      {/* Sample Size - Admin only */}
      {isAdmin && (
        <div className="mb-6">
          <label htmlFor="sampleSize" className="block text-sm font-medium text-gray-700 mb-2">
            Number of Agents
            <span className="text-gray-500 font-normal ml-2">(optional, 1-126)</span>
          </label>
          <input
            id="sampleSize"
            type="number"
            min={1}
            max={126}
            className={inputClasses}
            value={sampleSize}
            onChange={(e) => {
              const raw = e.target.value;
              if (raw === '') { setSampleSize(126); handleFormChange(); return; }
              const val = parseInt(raw, 10);
              if (!isNaN(val)) setSampleSize(Math.min(126, Math.max(1, val)));
              handleFormChange();
            }}
            onBlur={() => {
              if (sampleSize < 1) setSampleSize(1);
            }}
            placeholder="Default: 126"
          />
        </div>
      )}

      {/* Population - Admin and Demo users */}
      {(isAdmin || isDemoUser) && (
        <div className="mb-6">
          <label htmlFor="population" className="block text-sm font-medium text-gray-700 mb-2">
            Population <span className="text-red-500">*</span>
          </label>
          {populationsLoading ? (
            <div className="text-sm text-gray-400 py-2">Loading populations...</div>
          ) : populations.length > 0 ? (
            <select
              id="population"
              className={selectClasses}
              value={population}
              onChange={(e) => { setPopulation(e.target.value); handleFormChange(); }}
            >
              {populations.map(p => (
                <option key={p.name} value={p.name}>
                  {p.name.replace(/_/g, ' ')} ({isDemoUser ? DEMO_DISPLAY_N_AGENTS : p.agent_count} agents)
                </option>
              ))}
            </select>
          ) : (
            <input
              id="population"
              type="text"
              className={inputClasses}
              value={population}
              onChange={(e) => { setPopulation(e.target.value); handleFormChange(); }}
              placeholder="e.g., population_vitamins"
            />
          )}
        </div>
      )}

      {/* Concepts Section */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <label className="block text-sm font-medium text-gray-700">
            {mode === 'single' ? 'Concept Details' : `Concepts (${concepts.length})`}
          </label>
          {mode === 'multi' && concepts.length < 2 && (
            <span className="text-xs text-amber-600">Minimum 2 concepts required</span>
          )}
        </div>

        <div className="space-y-3">
          {concepts.map((concept, index) => (
            <React.Fragment key={concept.id}>
              <ConceptCard
                concept={concept}
                index={index}
                totalCount={concepts.length}
                isExpanded={expandedIds.has(concept.id)}
                onToggleExpand={() => {
                  setExpandedIds(prev => {
                    const next = new Set(prev);
                    if (next.has(concept.id)) {
                      next.delete(concept.id);
                    } else {
                      next.add(concept.id);
                    }
                    return next;
                  });
                }}
                onUpdate={(text) => updateConcept(concept.id, text)}
                onPrefillTemplate={() => prefillTemplate(concept.id)}
                onDuplicate={() => duplicateConcept(concept.id)}
                onDelete={() => removeConcept(concept.id)}
                canDelete={mode === 'multi' && concepts.length > 2}
                showActions={mode === 'multi'}
                onDragStart={(e) => handleDragStart(e, index)}
                onDragEnd={handleDragEnd}
                onDragOver={(e) => handleDragOver(e, index)}
                onDrop={(e) => handleDrop(e, index)}
                isDragging={draggedIndex === index}
                isDropTarget={dropTargetIndex === index}
                onParseImage={(file) => handleParseImage(concept.id, file)}
                isParsingImage={parsingImageForId === concept.id}
              />

              {/* Insert button between cards */}
              {mode === 'multi' && index < concepts.length - 1 && (
                <div className="flex items-center justify-center py-1 group">
                  <div className="flex-1 h-px bg-gray-200" />
                  <button
                    type="button"
                    onClick={() => insertConceptAt(index + 1)}
                    className="mx-2 flex items-center gap-1 px-3 py-1 text-xs font-medium rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    style={{ backgroundColor: BRAND.accent, color: BRAND.dark }}
                  >
                    <Plus className="w-3 h-3" />
                    Insert
                  </button>
                  <div className="flex-1 h-px bg-gray-200" />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>

        {/* Add new concept button */}
        {mode === 'multi' && (
          <button
            type="button"
            onClick={addConceptAtEnd}
            className="mt-4 w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-sm font-medium text-gray-500 hover:border-[#07E5D1] hover:text-[#07E5D1] transition-colors flex items-center justify-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add New Concept
          </button>
        )}
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <Button
          variant="primary"
          onClick={handleContinue}
          disabled={!isFormValid || isSubmitting}
          withArrow
        >
          {isSubmitting
            ? 'Processing...'
            : needsSegmentStep
              ? 'Continue to Segment Selection'
              : onSavePayload
                ? 'Continue'
                : mode === 'multi'
                  ? `Run Multi-Concept Test (${concepts.length})`
                  : 'Run Concept Test'
          }
        </Button>
      </div>
    </div>
  );

  // Render segment selection step using the VitaminSegmentSelector component
  const renderSegmentSelectionStep = () => (
    <VitaminSegmentSelector
      segments={SEGMENT_OPTIONS}
      selectedSegments={selectedSegments}
      onSegmentToggle={toggleSegment}
      onSelectAll={selectAllSegments}
      onClearAll={clearAllSegments}
      onBack={handleBackFromSegments}
      onNext={handleSubmit}
      isSubmitting={isSubmitting}
      submitButtonText={mode === 'multi' ? `Run Multi-Concept Test (${concepts.length})` : 'Run Concept Test'}
      isDemoUser={isDemoUser}
    />
  );

  // Return based on current step
  if (currentStep === 'segment-selection') {
    return renderSegmentSelectionStep();
  }

  return renderConceptDetailsStep();
};

export default VitaminConceptTestForm;
